/**
 * Design tokens for @ksana/vira-ui.
 * Seeded from web/src/utils/theme.ts plus Figma accents.
 */

export const colors = {
  neutral: {
    white: "#FFFFFF",
    clay: "#C8CDDB",
    cement: "#909CBC",
    asphalt: "#545E78",
    denim: "#2F3B5A",
    denim_08: "rgba(47, 59, 90, 0.8)",
    space: "#1E263C",
    space_08: "rgba(30,38,60,0.8)",
    surface_subtle: "#EDEEEF",
    ink: "#0B1950",
  },
  opacities: {
    black03: "rgba(30,38,60,0.03)",
  },
  primary: {
    cloud: "#EEF5FD",
    cloud_05: "rgba(238, 245, 253, 0.5)",
    mist: "#DBE5F8",
    ice: "#C1CEF3",
    ice_03: "rgba(193,206,243,0.3)",
    periwinkle: "#7591E4",
    shadow: "#4F66AA",
    midnight: "#3D4F83",
  },
  secondary: {
    blush: "#FFDBD9",
    peach: "#FFC4BD",
    rose: "#FF9D94",
    grapefruit: "#FF776C",
    grapefruit_03: "rgba(255,119,108,0.3)",
    plum: "rgba(137,98,208, 0.2)",
    raspberry: "#FF5858",
    crimson: "#E23E4F",
  },
  tertiary: {
    tangerine: "#FFAC62",
    violet: "#8962D0",
    orange: "#FF806C",
  },
  /** Vira UI surface / accent tokens (from Figma) — light (default) */
  vira: {
    canvas: "#E8E6E1",
    pageBg: "#F7F8FB",
    border: "#E4E7EF",
    ink: "#1B2A4A",
    muted: "#4A5A70",
    subtle: "#8B94A3",
    accent: "#C05A21",
    accentSoft: "#E87B3E",
    primaryBtn: "#2E3766",
    navActiveBg: "#F2F4FC",
    lavender: "#EEF0FA",
    lavenderBorder: "#D9DEF2",
    lavenderText: "#3D4785",
    chipViraSafeBg: "#FBE9E9",
    chipViraSafeText: "#A93B32",
    chipViraPathBg: "#E7F2EC",
    chipViraPathText: "#3F7D62",
    chipDbtBg: "#EEF0FA",
    chipDbtText: "#3D4785",
    /** Disabled / soft UI text on light surfaces */
    disabledText: "#AEB4C6",
    softSurface: "#F1F2F6",
    softBorder: "#DDE1EA",
  },
  graphs: {
    cravings: {
      intensity500: "#9572d5",
      intensity400: "#9572d5",
      intensity300: "#9e7ed8",
      intensity200: "#ac91de",
      intensity100: "#b8a1e3",
    },
    enjoyment: {
      intensity100: "color-mix(in oklab, #7591E4 82%, white)",
      intensity200: "color-mix(in oklab, #7591E4 90%, white)",
      intensity300: "#7591E4",
    },
    alerts: {
      positive: "#007546",
      negative: "#D33444",
    },
    myPlaces: {
      safePlace: "#33C189",
      riskyPlace: "#D33444",
    },
    labels: {
      colorText: "#737373",
    },
  },
  trendingData: {
    orange: { light: "#FFE4CC", dark: "#FFAC62" },
    red: { light: "#FFB2B2", dark: "#FF5858" },
    blue: { light: "#CCF6FF", dark: "#0BBBE2" },
    green: { light: "#E0FFE5", dark: "#92DB9D" },
  },
  questionnaires: {
    green: { light: "#8CDAB5", dark: "#19B66C" },
    yellow: { light: "#FFE895", dark: "#FDCD25" },
    orange: { light: "#FFBD80", dark: "#FF7A00" },
    red: { light: "#FF8383", dark: "#FF0707" },
    gray: { light: "#a9a9a9", dark: "#808080" },
  },
  safetyPlan: {
    grayscale: {
      black10: "rgba(0, 0, 0, 0.10)",
      white75: "rgba(255, 255, 255, 0.75)",
      white85: "rgba(255, 255, 255, 0.85)",
    },
    graphText: "#737373",
    cardSecondaryBackground: "#FCFCFC",
    background: "rgba(0, 0, 0, 0.03)",
    mediumBlue: "rgba(66, 79, 128, 0.70)",
    ink: "#0B1950",
    midnight: "#424F80",
    ash: "#E9EBF2",
    sky: "#0BBBE2",
    lime: "#33C189",
    ice: "#BAC8F1",
    cloud: "#EEF2FF",
    danger: "#D33444",
    dangerTint: "rgba(211, 52, 68, 0.03)",
  },
  schedule: {
    MOTIVATIONAL_INTERVIEWING: {
      border: "#7591E4",
      background: "rgba(117, 145, 228, 0.2)",
    },
    BEHAVIORAL_ACTIVATION: {
      border: "#8962D0",
      background: "rgba(137, 98, 208, 0.2)",
    },
    COGNITIVE_RESTRUCTURING: {
      border: "#0BBBE2",
      background: "rgba(11, 187, 226, 0.2)",
    },
    MINDFULNESS_AND_RELAXATION: {
      border: "#FFAC62",
      background: "rgba(255, 172, 98, 0.2)",
    },
    EXPOSURE_THERAPY: {
      border: "#92DB9D",
      background: "rgba(146, 219, 157, 0.2)",
    },
    SLEEP_IMPROVEMENT: {
      border: "#3D4F83",
      background: "rgba(61, 79, 131, 0.2)",
    },
    ASSERTIVENESS_TRAINING: {
      border: "#E23E4F",
      background: "rgba(226, 62, 79, 0.2)",
    },
    RELAPSE_PREVENTION: {
      border: "#14D0BA",
      background: "rgba(20, 208, 186, 0.2)",
    },
    PRACTITIONER_DEFINED: {
      border: "#FF806C",
      background: "rgba(255, 128, 108, 0.2)",
    },
    EMOTIONAL_COOLDOWN: {
      border: "#9999ff",
      background: "#9999ff",
    },
    DBT_MINDFULNESS: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    DBT_DISTRESS_TOLERANCE: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    DBT_EMOTION_REGULATION: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    DBT_INTERPERSONAL_EFFECTIVENESS: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    DBT_WALKING_THE_MIDDLE_PATH: {
      border: "#D014BE",
      background: "rgba(208, 20, 190, 0.2)",
    },
    KEEP_ME_CALM: {
      border: "#4C288E",
      background: "#F5F1F9",
    },
    KEEP_ME_SAFE: {
      border: "#D33444",
      background: "#FEF6F7",
    },
    QUESTIONNAIRES: {
      border: "#4F66AA",
      background: "rgba(193, 206, 243, 1)",
    },
  },
} as const

export type ViraPalette = {
  -readonly [K in keyof typeof colors.vira]: string
}

export type ViraThemeMode = "light" | "dark"

/**
 * Dark Vira surfaces — denim/space/ink backgrounds with lifted text & accents.
 * Same keys as `colors.vira` so `palette.vira` stays shape-stable across modes.
 */
export const viraDark: ViraPalette = {
  canvas: colors.neutral.space,
  pageBg: "#252E48",
  border: "#3A4560",
  /** Near-white primary text — high contrast on dark navy surfaces */
  ink: "#FFFFFF",
  muted: "#F0F3FA",
  subtle: "#E4E8F4",
  accent: "#E87B3E",
  accentSoft: "#F0A06A",
  primaryBtn: colors.primary.periwinkle,
  navActiveBg: "rgba(117, 145, 228, 0.16)",
  lavender: "rgba(117, 145, 228, 0.14)",
  lavenderBorder: "rgba(117, 145, 228, 0.35)",
  lavenderText: colors.primary.ice,
  chipViraSafeBg: "rgba(226, 62, 79, 0.18)",
  chipViraSafeText: "#FF9D94",
  chipViraPathBg: "rgba(63, 125, 98, 0.22)",
  chipViraPathText: "#8CDAB5",
  chipDbtBg: "rgba(117, 145, 228, 0.16)",
  chipDbtText: colors.primary.ice,
  disabledText: "#8A93A8",
  softSurface: "#2A3552",
  softBorder: "#44506C",
}

export const viraLight: ViraPalette = { ...colors.vira }

export function getViraPalette(mode: ViraThemeMode = "light"): ViraPalette {
  return mode === "dark" ? viraDark : viraLight
}

/**
 * Client Detail / Vira Pro type scale — four roles only.
 * Prefer `Heading` / these tokens over one-off px values.
 */
export const typeScale = {
  /** Page title (one per pane) + client name */
  page: 27,
  /** Section titles and card titles (same size — hierarchy via weight/placement) */
  heading: 15,
  /** Body, nav, buttons, metric values, chart tick labels */
  body: 14,
  /** Eyebrows, group labels (TREAT), badges, chart axis titles, dates */
  meta: 12,
} as const

export type TypeScaleRole = keyof typeof typeScale

export const typography = {
  fontFamily: {
    display: '"Poppins", "Lato", sans-serif',
    body: '"Karla", "Lato", sans-serif',
    legacy: '"Lato", sans-serif',
  },
  letterSpacing: 0.3,
  typeScale,
} as const

export const spacing = {
  unit: 8,
} as const

/** MUI-aligned breakpoint values (px). Use via `theme.breakpoints` in components. */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const

export type ViraBreakpoint = keyof typeof breakpoints

export const tokens = {
  colors,
  typography,
  typeScale,
  spacing,
  breakpoints,
  viraLight,
  viraDark,
} as const

export type ViraTokens = typeof tokens
